import {
  useCallback,
  useEffect,
  useState,
} from 'react'
import cx from 'classnames';

import {
  RootState,
  TunnelVersion,
  UiTheme,
} from '@greenkub-fronts/shared/interface'

import styles from './button.module.scss';
import { IconEnum } from '../../icon/icon.enum';
import Icon from '../../icon/icon';
import { useSelector } from 'react-redux'

export enum BtnSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  BIG = 'big',
  EMPTY = 'empty',
}

/**
 * Devrait être BtnStyle
 * et utiliser type pour inputType
 */
export enum BtnType {
  LINEAR = 'linear',
  SOLID = 'solid',
  GHOST = 'ghost',
}

export enum BtnLevel {
  PRIMARY = 'primary',
  PRIMARY_GEKA = 'primary-geka',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
  INACTIF = 'inactif',
  ALERT_SECONDARY = 'alert-secondary',
  ALERT = 'alert',
}

export enum BtnColor {
  WHITE = 'white',
  BLACK = 'black',
  PURPLE = 'purple',
  BASIC = 'basic',
  ALERT = 'alert',
  INFORMATION = 'information',
  ALERT_SECONDARY = 'alert-secondary',
  SUCCESS = 'success',
}

export enum InputType {
  SUBMIT = 'submit',
  BUTTON = 'button',
}

/**
 * Reprendre les ICON à l'aide d'un composant dédié
 */
export interface ButtonProps {
  children?: React.ReactNode;
  label?: string;
  color?: BtnColor;
  level?: BtnLevel;
  type?: BtnType;
  size?: BtnSize;
  loading?: boolean;
  selected?: boolean;
  IconeLeft?: IconEnum;
  IconeRight?: IconEnum;
  ImgLeft?: any;
  ImgRight?: any;
  className?: string;
  inputType?: InputType;
  onClick?: (e) => void;
  // onClick?: React.MouseEventHandler<HTMLButtonElement>;
  theme?: UiTheme;
  dataTestId?: string;
  style?: any;
}

export const Button = (props: ButtonProps) => {
  const {
    label,
    children,
    color,
    loading = false,
    type = BtnType.SOLID,
    size = BtnSize.MEDIUM,
    selected = false,
    onClick = () => false,
    IconeLeft,
    IconeRight,
    ImgLeft,
    ImgRight,
    className,
    inputType = InputType.BUTTON,
    theme = UiTheme.GK,
    dataTestId = '',
    style,
  } = props;

  const { version } = useSelector((state: RootState) => state.tunnel);

  const levelSetter = () => {
    if(props.level && props.level !== BtnLevel.PRIMARY) {
      return props.level
    }
    return (version === TunnelVersion.KIT) ? BtnLevel.PRIMARY_GEKA : BtnLevel.PRIMARY
  }

  const [level, setLevel] = useState<BtnLevel>(levelSetter());

  useEffect(() => {
    setLevel(levelSetter());
  }, [props.level, version]);

  /**
   * Bloquer le bouton lors de chargements
   */
  useEffect(() => {
    if (loading) {
      setLevel(BtnLevel.INACTIF);
    } else {
      setLevel(levelSetter())
    }
  }, [loading]);

  /**
   * Bloquer le onClick sur inactif
   */
  const onCustomClick = useCallback(
    (e) => {
      if (!loading && level !== 'inactif') {
        onClick(e);
      }
    },
    [loading, level, onClick]
  );

  /**
   * Définir les classes du bouton
   */
  const definedClass = cx(
    styles['button'],
    level && styles[level],
    type && styles[type],
    color && styles[color],
    size && styles[size],
    selected && styles['selected'],
    loading && styles['loading'],
    (IconeRight || IconeLeft || ImgLeft || ImgRight) && styles['icon-button'],
    theme && styles[theme],
    className
  );

  /**
   * Ajouter la gestion de isLoading dans ce template
   */
  const content = () => {
    if (loading) {
      return (
        <Icon
          name={type === 'linear' ? IconEnum.LOADER_GREEN : IconEnum.LOADER}
        />
      );
    }

    return (
      <>
        {IconeLeft && <Icon name={IconeLeft} />}
        {ImgLeft && <img src={ImgLeft} alt="" />}
        {label ? <span>{label}</span> : children}
        {IconeRight && <Icon name={IconeRight} />}
        {ImgRight && <img src={ImgRight} alt="" />}
      </>
    );
  };

  return (
    <button
      data-testid={dataTestId}
      className={definedClass}
      style={style}
      onClick={onCustomClick}
      type={inputType}
    >
      {content()}
    </button>
  );
};

export default Button;
